const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';
const PLATFORM_HOSTNAME = 'platforma.logoplan.pl';
const PLATFORM_LINK_CLICKED = 'platform_link_clicked';
const ALLOWED_EVENTS = new Set(['$pageview', '$pageleave', PLATFORM_LINK_CLICKED]);
const EMAIL = /[\w.+-]+@[\w-]+(\.[\w-]+)+/;
// tak samo jak w aplikacji: ph_keyword to fraza z wyszukiwarki, $fbp/$fbc to identyfikatory reklamowe Meta
const STRIPPED_KEY = /ph_keyword$|^\$fb[pc]$/i;
const REDACTED = '<redacted>';

// jawnie, bo zdalna konfiguracja projektu (wspólnego z aplikacją) potrafi włączyć zbieranie danych
const LOCKED_DOWN_OPTIONS = {
  defaults: '2026-08-30',
  autocapture: false,
  rageclick: false,
  capture_dead_clicks: false,
  capture_heatmaps: false,
  enable_heatmaps: false,
  capture_exceptions: false,
  capture_performance: false,
  disable_session_recording: true,
  disable_surveys: true,
  disable_conversations: true,
  disable_product_tours: true,
  disable_web_experiments: true,
  disable_external_dependency_loading: true,
  advanced_disable_flags: true,
  logs: { captureConsoleLogs: false },
};

const containsEmail = (value) => {
  try {
    return EMAIL.test(decodeURIComponent(value));
  } catch {
    return EMAIL.test(value);
  }
};

const sanitizeProperties = (properties) =>
  properties && Object.fromEntries(
    Object.entries(properties)
      .filter(([key]) => !STRIPPED_KEY.test(key))
      .map(([key, value]) => [
        key,
        typeof value === 'string' && containsEmail(value) ? REDACTED : value,
      ])
  );

export const sanitizeEvent = (event) => {
  if (!ALLOWED_EVENTS.has(event.event)) return null;

  return {
    ...event,
    properties: sanitizeProperties(event.properties),
    ...(event.$set && { $set: sanitizeProperties(event.$set) }),
    ...(event.$set_once && { $set_once: sanitizeProperties(event.$set_once) }),
  };
};

const destinationOf = (pathname) => {
  if (pathname.startsWith('/auth/login')) return 'login';
  if (pathname === '/auth' || pathname === '/auth/' || pathname.startsWith('/auth/signup')) return 'register';
  return 'other';
};

export const describePlatformLink = (link) => {
  let url;
  try {
    url = new URL(link.href);
  } catch {
    return null;
  }
  if (url.hostname !== PLATFORM_HOSTNAME) return null;

  return {
    destination: destinationOf(url.pathname),
    link_text: link.textContent.replace(/\s+/g, ' ').trim().slice(0, 100),
  };
};

const trackPlatformLinkClicks = (posthog) => {
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    const properties = link && describePlatformLink(link);
    if (!properties) return;
    posthog.capture(PLATFORM_LINK_CLICKED, properties, { transport: 'sendBeacon' });
  }, true);
};

export const initPosthogAnalytics = async () => {
  const key = process.env.GATSBY_POSTHOG_KEY;
  if (!key) return;

  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      api_host: process.env.GATSBY_POSTHOG_HOST || DEFAULT_POSTHOG_HOST,
      ...LOCKED_DOWN_OPTIONS,
      // platforma.logoplan.pl czyta to ciasteczko i przy rejestracji scala anonimowe id i UTM-y z kontem
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      person_profiles: 'identified_only',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      before_send: sanitizeEvent,
      loaded: (instance) => instance.register({ source: 'website' }),
    });
    trackPlatformLinkClicks(posthog);
  } catch (error) {
    console.warn('[posthog-analytics] SDK się nie uruchomił', error);
  }
};
